// V2 Checkout Redirect with Security & Performance Improvements
// Version 2.1 - Added BFCache (Back/Forward) cleanup handlers
(function() {
    'use strict';
    
    // ===== CONFIGURATION =====
    const CONFIG = {
        MAIN_SITE_DOMAIN: 'upstateelevator.com', // Change this to your main site domain
        TEST_MODE: false, // SET TO false WHEN READY FOR PRODUCTION
        TEST_PARAM: 'test_redirect', // URL parameter to enable test mode
        TEST_COOKIE: 'checkout_redirect_test', // Cookie to maintain test session
        EXCLUDED_PRODUCTS: [], // Optional: Add product IDs that shouldn't redirect
        MAX_URL_LENGTH: 2000, // Switch to POST if URL would be longer
        TRANSFER_TIMEOUT: 10 * 60 * 1000, // 10 minutes in milliseconds
        VERSION: '2.1'
    };
    
    // ===== TEST MODE CONTROLLER =====
    class TestModeController {
        constructor() {
            this.isTestSession = this.checkTestSession();
            this.debugMode = this.checkDebugMode();
        }
        
        checkTestSession() {
            // If not in test mode, redirect everyone
            if (!CONFIG.TEST_MODE) {
                return true;
            }
            
            // Check for test parameter in URL
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get(CONFIG.TEST_PARAM) === 'true') {
                this.enableTestSession();
                return true;
            }
            
            // Check for existing test cookie
            if (this.getCookie(CONFIG.TEST_COOKIE) === 'active') {
                return true;
            }
            
            return false;
        }
        
        checkDebugMode() {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('debug') === 'true';
        }
        
        enableTestSession() {
            // Set cookie for 24 hours
            const expires = new Date();
            expires.setTime(expires.getTime() + (24 * 60 * 60 * 1000));
            document.cookie = `${CONFIG.TEST_COOKIE}=active;expires=${expires.toUTCString()};path=/`;
            
            // Show test mode indicator
            this.showTestIndicator();
        }
        
        getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        }
        
        showTestIndicator() {
            if (document.getElementById('test-mode-indicator')) return;
            
            const indicator = document.createElement('div');
            indicator.id = 'test-mode-indicator';
            indicator.style.cssText = `
                position: fixed;
                bottom: 20px;
                left: 20px;
                background: #ff6b6b;
                color: white;
                padding: 10px 15px;
                border-radius: 5px;
                font-size: 12px;
                z-index: 99999;
                cursor: pointer;
            `;
            indicator.innerHTML = '🧪 Checkout Redirect Test Mode Active (V2)';
            indicator.onclick = () => {
                if (confirm('Disable test mode for this session?')) {
                    document.cookie = `${CONFIG.TEST_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                    location.reload();
                }
            };
            document.body.appendChild(indicator);
        }
    }
    
    // ===== MAIN CHECKOUT REDIRECT CLASS =====
    class CheckoutRedirect {
        constructor() {
            this.testMode = new TestModeController();
            this.isProcessing = false; // Prevent double-processing
            this.processingTimeout = null;
            
            // Only initialize if in test session or production mode
            if (this.testMode.isTestSession) {
                this.init();
                this.setupCheckoutInterceptors();
                this.setupBFCacheHandlers(); // <-- NEW: clean up overlay/state on Back/Forward restore
                this.log('V2 Checkout Redirect System Active');
            } else {
                this.log('V2 Checkout Redirect System Inactive (not in test session)');
            }
        }
        
        init() {
            // Listen for dynamic checkout buttons
            this.observeDynamicButtons();
            
            // Add visual feedback for test mode
            if (CONFIG.TEST_MODE && this.testMode.isTestSession) {
                this.testMode.showTestIndicator();
            }
        }

        // ---- NEW: Handle Back/Forward Cache restores so overlay doesn't get stuck ----
        setupBFCacheHandlers() {
            window.addEventListener('pageshow', (evt) => {
                // evt.persisted is true when the page is restored from the bfcache in most browsers
                const nav = performance.getEntriesByType?.('navigation')?.[0];
                const isBFCache = evt.persisted || nav?.type === 'back_forward';
                if (isBFCache) {
                    this.log('BFCache restore detected — clearing overlay & state');
                    this.hideLoadingOverlay();
                    this.isProcessing = false;
                    if (this.processingTimeout) {
                        clearTimeout(this.processingTimeout);
                        this.processingTimeout = null;
                    }
                }
            });

            // Extra safety: when tab becomes visible again (e.g., back), make sure overlay is gone
            document.addEventListener('visibilitychange', () => {
                if (!document.hidden) {
                    const overlay = document.getElementById('checkout-redirect-overlay');
                    if (overlay) this.hideLoadingOverlay();
                    this.isProcessing = false;
                }
            });
        }
        // ------------------------------------------------------------------------------

        setupCheckoutInterceptors() {
            // Intercept checkout button clicks with debouncing
            document.addEventListener('click', (e) => {
                if (this.shouldInterceptElement(e.target) && !this.isProcessing) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.log('Checkout intercepted via click');
                    this.initiateCheckoutRedirect();
                }
            }, true);
            
            // Intercept form submissions
            document.addEventListener('submit', (e) => {
                const form = e.target;
                if (form.action && form.action.includes('/checkout') && !this.isProcessing) {
                    e.preventDefault();
                    this.log('Checkout form submission intercepted');
                    this.initiateCheckoutRedirect();
                }
            }, true);
        }
        
        shouldInterceptElement(element) {
            if (!element || this.isProcessing) return false;
            
            // Check if element or parents are checkout-related
            let current = element;
            for (let i = 0; i < 5; i++) {
                if (!current) break;
                
                // Check various checkout indicators
                const name = current.getAttribute('name') || '';
                const href = current.getAttribute('href') || '';
                const className = current.className || '';
                const text = (current.textContent || current.innerText || '').toLowerCase();
                
                if (
                    name.includes('checkout') ||
                    href.includes('/checkout') ||
                    className.includes('checkout') ||
                    text.match(/^(checkout|check out|proceed to checkout)$/)
                ) {
                    return true;
                }
                
                current = current.parentElement;
            }
            
            return false;
        }
        
        observeDynamicButtons() {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) {
                            // Check for dynamic checkout buttons
                            if (node.classList && (
                                node.classList.contains('shopify-payment-button') ||
                                node.classList.contains('additional-checkout-buttons')
                            )) {
                                this.interceptDynamicCheckout(node);
                            }
                        }
                    });
                });
            });
            
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
        
        interceptDynamicCheckout(container) {
            const buttons = container.querySelectorAll('button');
            buttons.forEach(button => {
                button.addEventListener('click', (e) => {
                    if (!this.isProcessing) {
                        e.preventDefault();
                        e.stopPropagation();
                        this.log('Dynamic checkout button intercepted');
                        this.initiateCheckoutRedirect();
                    }
                }, true);
            });
        }
        
        async initiateCheckoutRedirect() {
            // Prevent double-processing
            if (this.isProcessing) {
                this.log('Already processing, skipping duplicate request');
                return;
            }
            
            this.isProcessing = true;
            
            // Set timeout to reset processing flag
            this.processingTimeout = setTimeout(() => {
                this.isProcessing = false;
            }, 30000); // 30 second timeout
            
            try {
                this.showLoadingOverlay();
                
                // Get current cart
                const cart = await this.getCart();
                
                if (!cart || cart.item_count === 0) {
                    alert('Your cart is empty');
                    this.hideLoadingOverlay();
                    this.isProcessing = false;
                    return;
                }
                
                // Check for excluded products
                if (this.hasExcludedProducts(cart)) {
                    this.log('Cart contains excluded products, allowing normal checkout');
                    this.hideLoadingOverlay();
                    this.isProcessing = false;
                    window.location.href = '/checkout';
                    return;
                }
                
                // Prepare cart data with enhanced security
                const cartData = this.prepareCartData(cart);
                
                // Determine transfer method based on size
                const redirectUrl = await this.buildRedirectUrl(cartData);
                
                this.log('Redirecting to main site...', redirectUrl);
                window.location.href = redirectUrl;
                
            } catch (error) {
                console.error('Checkout redirect failed:', error);
                this.hideLoadingOverlay();
                this.isProcessing = false;
                if (this.processingTimeout) {
                    clearTimeout(this.processingTimeout);
                    this.processingTimeout = null;
                }
                
                if (confirm('Unable to redirect to main checkout. Proceed with regular checkout?')) {
                    window.location.href = '/checkout';
                }
            }
        }
        
        async getCart() {
            const response = await fetch('/cart.js');
            return await response.json();
        }
        
        hasExcludedProducts(cart) {
            if (CONFIG.EXCLUDED_PRODUCTS.length === 0) return false;
            
            return cart.items.some(item => 
                CONFIG.EXCLUDED_PRODUCTS.includes(item.product_id.toString())
            );
        }
        
        prepareCartData(cart) {
            // Extract discount codes from cart object
            let discountCode = null;
            
            // Check multiple possible locations for discount
            if (cart.discount_code) {
                discountCode = cart.discount_code;
            } else if (cart.discounts && cart.discounts.length > 0) {
                discountCode = cart.discounts[0].code || cart.discounts[0].title;
            } else if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
                discountCode = cart.cart_level_discount_applications[0].title;
            }
            
            if (discountCode) {
                this.log('Discount code found:', this.maskSensitiveData(discountCode));
            }
            
            // Prepare cart data with timestamp for expiration
            const cartData = {
                items: cart.items.map(item => ({
                    sku: item.sku, // Unsanitized; destination normalizes/handles safely
                    quantity: Math.min(Math.max(1, item.quantity), 99), // Limit 1-99
                    product_title: this.sanitizeString(item.product_title),
                    variant_title: this.sanitizeString(item.variant_title),
                    handle: item.handle,
                    vendor: this.sanitizeString(item.vendor),
                    product_id: item.product_id,
                    variant_id: item.variant_id
                })),
                note: this.sanitizeString(cart.note),
                attributes: cart.attributes,
                original_total: cart.total_price,
                discount_code: discountCode,
                item_count: cart.item_count,
                source: window.location.hostname,
                timestamp: new Date().toISOString(),
                version: CONFIG.VERSION,
                test_mode: CONFIG.TEST_MODE
            };
            
            // Add simple hash for data integrity
            cartData.hash = this.generateHash(cartData);
            
            return cartData;
        }
        
        sanitizeString(str) {
            if (!str) return '';
            // Basic XSS prevention
            return str.toString()
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#x27;')
                .substring(0, 200); // Limit length
        }
        
        generateHash(data) {
            // Simple hash for basic integrity check
            const str = JSON.stringify(data.items) + data.timestamp + data.source;
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32bit integer
            }
            return Math.abs(hash).toString(36);
        }
        
        async buildRedirectUrl(cartData) {
            const dataString = JSON.stringify(cartData);
            
            // Check if URL would be too long
            if (dataString.length > CONFIG.MAX_URL_LENGTH) {
                this.log('Cart data too large for URL, using fallback method');
                // For now, still use URL but warn
                // In future, implement POST method here
            }
            
            // Encode cart data for URL
            const encodedData = btoa(dataString);
            let url = `https://${CONFIG.MAIN_SITE_DOMAIN}/pages/checkout-import?cart=${encodeURIComponent(encodedData)}`;
            
            // Add debug parameter if in debug mode
            if (this.testMode.debugMode) {
                url += '&debug=true';
            }
            
            return url;
        }
        
        showLoadingOverlay() {
            const overlay = document.createElement('div');
            overlay.id = 'checkout-redirect-overlay';
            overlay.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0,0,0,0.7);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 999999;
            `;
            overlay.innerHTML = `
                <div style="background: white; padding: 30px; border-radius: 8px; text-align: center;">
                    <div class="spinner" style="width: 50px; height: 50px; border: 3px solid #5c6ac4; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
                    <h3 style="margin: 0 0 10px 0;">Redirecting to Secure Checkout...</h3>
                    <p style="margin: 0; color: #666;">Transferring your cart to Upstate Elevator</p>
                    <p style="margin: 10px 0 0 0; font-size: 12px; color: #999;">V2 Enhanced Security</p>
                </div>
                <style>
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                </style>
            `;
            document.body.appendChild(overlay);
        }
        
        hideLoadingOverlay() {
            const overlay = document.getElementById('checkout-redirect-overlay');
            if (overlay) overlay.remove();
        }
        
        maskSensitiveData(str) {
            // For logging - show only first and last 2 characters
            if (!str || str.length < 5) return '***';
            return str.substring(0, 2) + '***' + str.substring(str.length - 2);
        }
        
        log(message, data = null) {
            if (this.testMode.debugMode) {
                console.log(`[V2 Checkout Redirect] ${message}`, data || '');
            }
        }
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => new CheckoutRedirect());
    } else {
        new CheckoutRedirect();
    }
})();